import { useState, useEffect } from "react";

import { DataFile, DataFileCreationChain } from "interfaces";

import Alert from "ui-components/Alert";
import Button from "ui-components/Button";
import Divider from "ui-components/Divider";
import RadioSwitch from "ui-components/Radio-Button";

import getCreationSteps from "modules/data-files/creation-steps";

import { useCreateDataMacro } from "modules/macros/api";
import InputField from "ui-components/Input-Field";

interface Props {
  dataFile: DataFile.DataFile;
  close: () => void;
  creationChain: DataFileCreationChain;
}

const Form = ({ dataFile, close, creationChain }: Props) => {
  const [startLink, setStartLink] = useState<number>(0);
  const [name, setName] = useState<string>();
  const [error, setError] = useState<string>();
  const [success, setSuccess] = useState<boolean>(false);

  const createDataFileMacro = useCreateDataMacro();

  const updateStartLink = (link: number) => {
    setStartLink(link);
    setError(undefined);
    setSuccess(false);
  };

  // Init
  useEffect(() => {
    const steps = getCreationSteps(creationChain, dataFile);
    setStartLink(steps.length - 1);
  }, [creationChain, dataFile, setStartLink]);

  const onSubmit = async () => {
    if (!name) {
      setError("You MUST set a Name");
      return;
    }
    const steps = getCreationSteps(creationChain, dataFile);
    // Zero-index
    const link = steps[startLink];
    let inputFiles: string[] = [];
    link.transform.forEach(({ files }) => inputFiles.push(...files));

    try {
      await createDataFileMacro({
        name,
        reference_data_file: dataFile.id,
        reference_input_files: Array.from(new Set(inputFiles)),
      });
      setSuccess(true);
    } catch (e) {
      if (e.non_field_errors) {
        setError(e.non_field_errors[0]);
      }
    }
  };

  const steps = getCreationSteps(creationChain, dataFile);

  return (
    <div className="text-left">
      <Alert variant="info">
        <p className="py-1 my-1">
          Create a MACRO which can take other files, and generate files exactly
          like <span className="italic underline">{dataFile.name}</span> (this
          one).
        </p>
        <p className="py-1 my-1">
          The Input Columns MUST be same as input to this file, and the exact
          same steps are performed to generates another file like this one.
        </p>
      </Alert>
      <div className="p-1 m-1">
        <p className="pt-2 mt-2 font-bold">Macro Steps</p>
        {steps.length >= 2 ? (
          <RadioSwitch
            label={`This file went through ${steps.length} transforms. You can start the MACRO at any of the steps. Only that step and the following steps will be part of the generated MACRO.`}
            options={[...steps].reverse().map((c, ind) => {
              const t = c.transform.map((t) => t.type).join(" + ");
              return {
                key: (steps.length - ind - 1).toString(),
                label: (
                  <p>
                    Step {ind + 1}: {t || "INGESTION (Original)"}
                  </p>
                ),
              };
            })}
            selected={startLink.toString()}
            onChange={(value) => updateStartLink(parseInt(value))}
          />
        ) : (
          <p>
            This file went through{" "}
            {steps[0].transform.map((t) => t.type).join(" + ")}.
          </p>
        )}
      </div>
      <Divider />
      <div className="p-1 m-1">
        <p className="font-bold">Macro Name</p>
        <InputField<string>
          type="text"
          value={name}
          onChange={setName}
          placeholder="Enter Name"
          help="Name that you can remember this by"
        />
      </div>
      <Divider />
      {error && <Alert variant="error">{error}</Alert>}
      {success && (
        <Alert variant="success">Successfully Created the Macro!!!</Alert>
      )}
      <p className="text-right">
        <Button color="gray" onClick={close}>
          Close
        </Button>
        <Button onClick={onSubmit}>Create Macro</Button>
      </p>
    </div>
  );
};

export default Form;
